import { NumberInput, ScrollArea, Table, Text } from '@mantine/core'
import { InterestRateParams, TermRates } from 'app/models/interest-rate'
import '../../../../styles/components/daily-rates.css'
import { formatDateToShow, stringToDate } from 'app/utils/util-functions'
import { ReactComponent as EditIcon } from 'app/public/generic-icons/edit-icon.svg'
import { useState } from 'react'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import CustomPopover from 'app/views/components/popover/custom-popover'
import { useDispatch } from 'react-redux'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { overrideRate } from 'app/state/ducks/interest-rates/thunks'
import { keyToContractPeriod } from 'app/models/dropdown-options'


interface Props {
  data: InterestRateParams[]
  indexType: string
}

const DailyRatesTable = ({ data, indexType }: Props) => {
  const dispatch = useDispatch()
  const getColumns = (indexType: string) => {
    const columnMappings: { [key: string]: { label: string; key: string }[] } = {
      'TermIndex': [
        { label: 'Overnight', key: 'overnight' },
        { label: 'One Week', key: 'oneWeek' },
        { label: 'Two Weeks', key: 'twoWeeks' },
        { label: 'Three Weeks', key: 'threeWeeks' },
        { label: 'One Month', key: 'oneMonth' },
        { label: 'Two Months', key: 'twoMonths' },
        { label: 'Three Months', key: 'threeMonths' },
        { label: 'Four Months', key: 'fourMonths' },
        { label: 'Five Months', key: 'fiveMonths' },
        { label: 'Six Months', key: 'sixMonths' },
        { label: 'Seven Months', key: 'sevenMonths' },
        { label: 'Eight Months', key: 'eightMonths' },
        { label: 'Nine Months', key: 'nineMonths' },
        { label: 'Ten Months', key: 'tenMonths' },
        { label: 'Eleven Months', key: 'elevenMonths' },
        { label: 'One Year', key: 'oneYear' },
      ],
      'FloatingIndex': [
        { label: 'Rate Value', key: 'floatingRate' },
      ],
      'OvernightIndex': [
        { label: 'Rate Value', key: 'overnightRate' },
      ]
    };

    return columnMappings[indexType] || [];
  };

  const getRateValue = (item: InterestRateParams, col: { key: string }, indexType: string) => {
    if (indexType === 'TermIndex') {
      return item.termRates?.[col.key as keyof TermRates] ?? '';
    }
    return item[col.key as keyof InterestRateParams] ?? '';
  };

  const columns = getColumns(indexType)
  const [editValue, setEditValue] = useState<number>(0);
  const [openedRow, setOpenedRow] = useState<number | null>(null);
  const [openedColumn, setOpenedColumn] = useState<number | null>(null);
  const [loading, setLoading] = useState(false)

  const onSave = async (col: any, item: InterestRateParams) => {
    setLoading(true)
    const result: any = await dispatch(overrideRate(item.indexId, item.effectiveDate, editValue.toString(), keyToContractPeriod[col.key]))
    if (result.success) {
      SuccessNotification({
        title: "Success",
        message: "Rate updated successfully",
      })
    } else {
      ErrorNotification({
        title: "Error",
        message: result.payload ?? '',
      })
    }
    setLoading(false)
    setOpenedRow(null);
    setOpenedColumn(null)
  }

  return (
    <ScrollArea className='daily-rates-table-scrollarea'>
      <div className='daily-rates-table-wrapper'>
        <Table>
          <thead>
            <tr className='daily-rates-thead-tr'>
              <th className='daily-rates-th'><Text className='daily-rates-th-text'>Effective Date</Text></th>
              {columns.map((col, idx) => (
                <th className='daily-rates-th' key={idx}><Text className='daily-rates-th-text'>{col.label}</Text></th>
              ))}
            </tr>
          </thead>
          <tbody className='daily-rates-tbody'>
            {data.map((item, i) => (
              <tr key={i} className='daily-rates-tbody-tr'>
                <td>
                  <Text className='daily-rates-td-text'>{formatDateToShow(item?.effectiveDate ? stringToDate(item.effectiveDate) : undefined)}</Text>
                </td>
                {columns.map((col, idx) => (
                  <td key={idx}> <div className='daily-rates-td-value'>
                    <Text className='daily-rates-td-text'>{String(getRateValue(item, col, indexType))}</Text>

                    {String(getRateValue(item, col, indexType)) !== '' ? <CustomPopover
                      opened={i === openedRow && idx === openedColumn}
                      onClose={() => {
                        setOpenedRow(null);
                        setOpenedColumn(null)
                      }
                      }
                      targetComponent={
                        <EditIcon style={{ width: '30px', cursor: "pointer" }}
                          onClick={() => {

                            setOpenedRow(i);
                            setOpenedColumn(idx)
                            setEditValue(Number(String(getRateValue(item, col, indexType))))
                          }}
                        />
                      }
                      position="bottom"
                      withArrow={false}
                    >
                      <NumberInput
                        precision={5}
                        p="6px"
                        type="text"
                        value={editValue}
                        onChange={(e) => {
                          if (e == '') {
                            setEditValue(0);
                            return
                          }
                          setEditValue(e);

                        }}
                      />
                      <div className='daily-rates-edit-button-wrapper'>
                        <PrimaryButton
                          loading={loading}
                          text="Cancel"
                          textSize='14px'
                          textColor='#3d3d3d'
                          className='iro-form-button'
                          type='button'
                          w='100%'
                          variant='outline'
                          bg='white'
                          hoverBackground='white'
                          border='1px solid #B1B1B1'
                          onClick={() => {
                            setOpenedRow(null);
                            setOpenedColumn(null)
                          }}
                        />
                        <PrimaryButton
                          loading={loading}
                          className='daily-rates-edit-button'
                          onClick={() => onSave(col, item)}
                        > Save </PrimaryButton>
                      </div>
                    </CustomPopover> : null}</div></td>
                )
                )}
              </tr>
            )
            )}
          </tbody>
        </Table>
      </div>
    </ScrollArea >
  );
}

export default DailyRatesTable